import React, { createContext, useState, useEffect, useContext } from 'react';
import packagesData from '../data/packages.json';

const PackageContext = createContext();

export const usePackages = () => {
  const context = useContext(PackageContext);
  if (!context) {
    throw new Error('usePackages must be used within a PackageProvider');
  }
  return context;
};

export const PackageProvider = ({ children }) => {
  const [packages, setPackages] = useState([]);
  const [categories] = useState(['All', 'Asia', 'Europe', 'Explore India', 'Seasonal Packages']);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // Load packages from JSON
    setPackages(packagesData);
  }, []);

  // Filter packages by category, visibility, and search query
  const getFilteredPackages = () => {
    let filtered = packages.filter(pkg => pkg.visible !== false);
    
    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(pkg => pkg.category === selectedCategory);
    }
    
    // Filter by search query
    if (searchQuery && searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(pkg => 
        pkg.title.toLowerCase().includes(query) ||
        pkg.description.toLowerCase().includes(query) ||
        (pkg.highlights && pkg.highlights.some(h => h.toLowerCase().includes(query)))
      );
    }
    
    return filtered;
  };

  // Add a new package
  const addPackage = (newPackage) => {
    const packageWithId = {
      ...newPackage,
      id: packages.length > 0 ? Math.max(...packages.map(p => p.id)) + 1 : 1,
      visible: true
    };
    setPackages([...packages, packageWithId]);
  };

  // Update an existing package
  const updatePackage = (id, updatedPackage) => {
    setPackages(packages.map(pkg => 
      pkg.id === id ? { ...pkg, ...updatedPackage } : pkg
    ));
  };

  // Delete a package
  const deletePackage = (id) => {
    setPackages(packages.filter(pkg => pkg.id !== id));
  };

  // Toggle package visibility
  const toggleVisibility = (id) => {
    setPackages(packages.map(pkg => 
      pkg.id === id ? { ...pkg, visible: !pkg.visible } : pkg
    ));
  };

  const value = {
    packages,
    categories,
    selectedCategory,
    setSelectedCategory,
    searchQuery,
    setSearchQuery,
    getFilteredPackages,
    addPackage,
    updatePackage,
    deletePackage,
    toggleVisibility
  };

  return (
    <PackageContext.Provider value={value}>
      {children}
    </PackageContext.Provider>
  );
};
